import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ShoppingCartIcon,
  StarIcon,
  ArrowLeftIcon,
  MinusIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import api from '../services/api';
import useCartStore from '../store/cartStore';
import Loading from '../components/common/Loading';
import { toast } from 'react-hot-toast';

const ProductDetailPage = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const addItem = useCartStore((state) => state.addItem);

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  const fetchProduct = async () => {
    try {
      const res = await api.get(`/products/${slug}/`);
      setProduct(res.data);
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const getName = () => {
    if (!product) return '';
    const lang = i18n.language;
    if (lang === 'ar' && product.name_ar) return product.name_ar;
    if (lang === 'en' && product.name_en) return product.name_en;
    if (lang === 'ku' && product.name_ku) return product.name_ku;
    return product.name;
  };

  const getDescription = () => {
    if (!product) return '';
    const lang = i18n.language;
    if (lang === 'ar' && product.description_ar) return product.description_ar;
    if (lang === 'en' && product.description_en) return product.description_en;
    if (lang === 'ku' && product.description_ku) return product.description_ku;
    return product.description;
  };

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addItem(product);
    }
    toast.success(`تم إضافة ${quantity} من ${getName()} إلى السلة`);
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<StarIconSolid key={i} className="w-5 h-5 text-yellow-400" />);
      } else {
        stars.push(<StarIcon key={i} className="w-5 h-5 text-gray-300" />);
      }
    }
    return stars;
  };

  if (loading) {
    return <Loading />;
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h2 className="text-2xl font-bold mb-4">المنتج غير موجود</h2>
        <Link to="/products" className="text-blue-600 hover:underline">
          العودة للمنتجات
        </Link>
      </div>
    );
  }

  const images = product.images && product.images.length > 0 ? product.images : ['/placeholder.jpg'];
  const isAvailable = product.is_available && product.stock_quantity > 0;
  const maxQuantity = Math.min(product.stock_quantity || 1, 99);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <Link
          to="/products"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 mb-6 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          العودة للمنتجات
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images */}
          <div>
            <div className="aspect-square bg-gray-100 rounded-2xl overflow-hidden mb-4 shadow-lg">
              <img
                src={images[selectedImage]}
                alt={getName()}
                className="w-full h-full object-cover"
              />
            </div>
            
            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-3">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`aspect-square bg-gray-100 rounded-xl overflow-hidden border-2 transition-all ${
                      selectedImage === index
                        ? 'border-blue-600 ring-2 ring-blue-200'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Info */}
          <div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">{getName()}</h1>
            
            {product.smart_rating > 0 && (
              <div className="flex items-center gap-2 mb-4">
                {renderStars(product.smart_rating)}
                <span className="text-lg text-gray-600">
                  ({product.smart_rating.toFixed(1)})
                </span>
              </div>
            )}
            
            {product.brand_name && (
              <p className="text-gray-600 mb-2">
                الماركة: <span className="font-semibold text-gray-900">{product.brand_name}</span>
              </p>
            )}
            
            {product.category_name && (
              <p className="text-gray-600 mb-6">
                التصنيف: <span className="font-semibold text-gray-900">{product.category_name}</span>
              </p>
            )}
            
            <div className="mb-6">
              {product.original_price && product.original_price > product.price && (
                <p className="text-gray-400 line-through text-xl mb-2">
                  {product.original_price.toLocaleString()} د.ع
                </p>
              )}
              <p className="text-5xl font-bold text-blue-600">
                {product.price?.toLocaleString() || '0'} د.ع
              </p>
            </div>
            
            {isAvailable ? (
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-6">
                <p className="text-green-700 font-semibold flex items-center gap-2">
                  <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                  متوفر ({product.stock_quantity} قطعة)
                </p>
              </div>
            ) : (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
                <p className="text-red-700 font-semibold">
                  غير متوفر حالياً
                </p>
              </div>
            )}
            
            <div className="mb-6">
              <label className="block mb-3 font-semibold text-gray-900">الكمية</label>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-12 h-12 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors flex items-center justify-center"
                >
                  <MinusIcon className="w-5 h-5" />
                </button>
                <span className="text-2xl font-bold w-16 text-center">{quantity}</span>
                <button
                  onClick={() => setQuantity(Math.min(maxQuantity, quantity + 1))}
                  disabled={quantity >= maxQuantity}
                  className="w-12 h-12 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlusIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <button
              onClick={handleAddToCart}
              disabled={!isAvailable}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-4 rounded-xl font-bold text-lg hover:shadow-xl disabled:bg-gray-300 disabled:cursor-not-allowed transition-all transform hover:scale-105 flex items-center justify-center gap-3 mb-4"
            >
              <ShoppingCartIcon className="w-6 h-6" />
              {isAvailable ? 'أضف للسلة' : 'غير متوفر'}
            </button>
            
            <div className="border-t pt-8 mt-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-900">الوصف</h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {getDescription() || 'لا يوجد وصف متاح'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
